/**
 * Collection in-card Quick Add — lazy loader + hover/interaction state
 * machine.
 *
 * Renders nothing itself. The first time a shopper opens a card's
 * <details data-quick-add-inline> (snippets/quick-add-inline.liquid,
 * hover on desktop / click or keyboard everywhere), this fetches that
 * product's existing `?section_id=api-product-upsell` markup — the exact
 * same endpoint and [data-api-content] extraction the theme's
 * QuickAddProduct custom element (assets/theme.dev.js / theme.js, not
 * edited here) already uses for its modal — and injects it into the
 * card's own panel instead of into a document.body-appended <dialog>.
 *
 * The injected markup is the theme's real <product-info> / <variant-selects> /
 * <product-form>, unmodified. assets/product-info.js (already loaded, not
 * edited here) auto-initializes it via the browser's normal custom-element
 * upgrade — no variant, price, availability or add-to-cart logic is
 * reimplemented here.
 *
 * STATE MACHINE (data-state on the <details>):
 *   closed -> loading -> open -> adding -> open (on error) | closed (on success)
 * Every transition into/out of "adding" is driven by real events the
 * theme's own cart JS already dispatches (theme:product:add /
 * theme:product:add-error, both fired on the button, both bubble — see
 * assets/theme.dev.js CartDrawer#addToCart / #addToCartError). No
 * MutationObserver anywhere in this file, and no setTimeout in the desktop
 * state machine (the only timer is the mobile-trigger recovery watchdog at
 * the bottom, which does not touch this state machine).
 *
 * OPEN: delegated `mouseover` on `document` opens a card's Quick Add the
 * moment the pointer enters it (or any descendant) — no per-card binding
 * needed for this direction, and it works on cards Shopify's AJAX
 * collection filter/sort injects later, since the listener was never
 * attached to individual (old) nodes in the first place.
 *
 * CLOSE: this file previously closed on a delegated `mouseout` +
 * `relatedTarget` check. That was replaced — first mouseenter/mouseleave
 * turned out to fire once per ancestor level between the old and new
 * target (not once for the outermost changed box), then the
 * mouseover/mouseout + relatedTarget replacement was itself too fragile in
 * Safari: injecting the fetched Quick Add markup into the panel can make
 * Safari emit a mouseout with relatedTarget === null while the pointer is
 * still visually over the card, which was being (mis)read as "the
 * customer left."
 *
 * The durable fix: a single `pointerleave` listener bound directly to each
 * card's own stable element (ensureCardExitListener/boundCards below).
 * pointerleave doesn't bubble and is computed by the browser from real
 * geometric containment of the whole element and its descendants — it
 * fires exactly once, only when the pointer genuinely leaves the entire
 * card (image, swatches, size buttons, the open panel, title, price all
 * included), regardless of DOM mutations happening inside it. Bound lazily
 * the first time a card is hovered (WeakSet-deduped, so re-hovering the
 * same card never adds a second listener) rather than at page load, so
 * cards Shopify's AJAX filter/sort renders later are covered automatically
 * the first time the pointer reaches them — a WeakSet holds no strong
 * reference, so a replaced card's old listener is simply garbage collected
 * with it, no manual cleanup needed.
 */
(function () {
  'use strict';

  var SELECTOR_DETAILS = '[data-quick-add-inline]';
  var SELECTOR_PANEL = '[data-quick-add-inline-panel]';
  // The whole card (grid-item.product-item), not just the image — this is
  // the stable, persistent node the theme itself never replaces (only the
  // <variant-selects> deep inside the fetched panel gets swapped on option
  // change), and hovering anywhere on the card — including the title/price
  // area, not only the photo — should reveal Quick Add.
  var SELECTOR_HOVER_ZONE = '.product-item';
  var SELECTOR_SUBMIT = '[data-add-to-cart]';
  var SELECTOR_SIZE_FIELDSET = '.radio__fieldset:not(.radio__fieldset--swatches)';
  // Its OWN section, not the sheet's: two fetches of one section for one product
// render identical radio names, and the sheet then loses its checked state.
var SECTION_ID = 'api-quick-add-inline';
  // Card-image sync (color swatch selected inside the panel -> card photo).
  // Every name/class here is the theme's own, already-shipping convention —
  // see GridSwatch.showVariantImage/hideVariantImages in theme.dev.js, which
  // already toggles the very same elements on hover of the card's OWN outer
  // swatch row. We are not introducing a second image system, only invoking
  // the existing one from a second trigger.
  var SELECTOR_CHECKED_SWATCH = '.radio__fieldset--swatches input[type="radio"]:checked';
  var SELECTOR_PRODUCT_IMAGE = '[data-product-image]';
  var SELECTOR_IMAGE_UNAVAILABLE_NOTICE = '[data-image-unavailable-notice]';
  var ATTR_VARIANT_TITLE = 'data-variant-title';
  var CLASS_IMAGE_VISIBLE = 'is-visible';
  // Mirrors data-state on the card itself (see setState) so CSS can suspend
  // the card's own separate outer swatch-hover image-swap (GridSwatch) for
  // as long as Quick Add owns the visible photo — see collections.css.
  var CLASS_CARD_QUICK_ADD_ACTIVE = 'quick-add-inline-active';
  // Set only once the shopper has actually picked a colour in the panel, and
  // cleared when the card closes. Opening the tray used to be enough to
  // suppress the card's hover imagery, which meant a desktop shopper could
  // never see a product's second photo at all: the tray opens on hover, so the
  // suppressing state was always true exactly while hovering. Colour-synced
  // media only needs to win once there IS a chosen colour to be authoritative
  // about - see collections.css.
  var CLASS_CARD_COLOUR_SELECTED = 'quick-add-inline-colour-selected';

  function getPanel(details) {
    return details.querySelector(SELECTOR_PANEL);
  }

  function setState(details, state) {
    details.dataset.state = state;
    // Single choke point for every state transition this file makes, so
    // "suspend the card's native swatch-hover image-swap while Quick Add
    // owns the photo, restore it the instant we're closed" needs no new
    // transition logic of its own — it just mirrors data-state.
    var card = details.closest(SELECTOR_HOVER_ZONE);
    if (card) {
      card.classList.toggle(CLASS_CARD_QUICK_ADD_ACTIVE, state !== 'closed');
      // Closing resets the colour-authority state, so the next hover starts
      // from the card's normal media behaviour instead of staying suppressed.
      if (state === 'closed') card.classList.remove(CLASS_CARD_COLOUR_SELECTED);
      if (state === 'closed') card.dispatchEvent(new CustomEvent('theme:card:quick-add-closed', {bubbles: true}));
    }
  }

  function getState(details) {
    return details.dataset.state || 'closed';
  }

  function buildRequestUrl(productUrl) {
    var separator = productUrl.indexOf('?') === -1 ? '?' : '&';
    return productUrl + separator + 'section_id=' + SECTION_ID;
  }

  // Root cause of cross-card variant/size corruption: sections/api-
  // product-upsell.liquid builds every id it renders (the add-to-cart
  // <form>'s own id included) from section.id, which the Section Rendering
  // API resolves to the same constant string for every product fetched
  // through this endpoint — confirmed live: two different products' fetched
  // panels produce byte-identical form ids. assets/product-info.js (theme.js,
  // not edited here) hardcodes `#product-form-${section}` /
  // `#product-form-installment-${section}` to find and update the variant-id
  // input after every option change, so that id cannot be made unique per
  // product without breaking that lookup. The only safe fix is structural:
  // never let two of these same-id forms exist in the live DOM at once.
  //
  // Enforced at exactly one chokepoint — here, immediately before a new
  // panel's markup is injected — rather than at every place a panel could
  // conceivably be closed. An earlier version of this fix tried the latter
  // (resetting on closeDetails/forceCloseDetails/closeOthers) and missed the
  // native toggle-close path entirely (clicking an open <summary> again
  // closes the <details> natively, with no JS call in between), silently
  // reintroducing the exact collision it was meant to prevent. A load-time
  // chokepoint is correct regardless of how the previous panel stopped being
  // the open one, including cases with no explicit "close" at all.
  //
  // activePanel/activeAbortController are module-level, not per-panel: the
  // real invariant is "at most one of these colliding-id forms is live in
  // the whole document," not a per-card concern.
  var activePanel = null;
  var activeAbortController = null;

  // Second race this file has to guard against, distinct from the id
  // collision above: activateDetails() (below) runs from two entry points —
  // synchronously from mouseover (for hover's fetch-on-intent perf), and
  // from the native `toggle` event, which — unlike the mouseover call — is
  // always a QUEUED task, never synchronous with the click/hover that
  // caused it. Gliding the pointer from card A directly onto card B queues
  // toggle tasks for both before either runs: by the time A's task fires,
  // B's mouseover has already evicted A's panel, so A's task sees a
  // reset/unloaded panel and — with nothing to say "this card's open was
  // already fully handled" — would call activateDetails(A) again, evicting
  // B (the card the pointer is actually on now) right back out and closing
  // it via that call's own closeOthers(A). B's own queued task then finds
  // itself already closed and no-ops, leaving B dead until a fresh, isolated
  // hover. Confirmed live via mouse-glide and rapid click simulation.
  //
  // hoverActivatedDetails marks a details element whose open was already
  // fully handled synchronously by mouseover — activateDetails, closeOthers
  // and all. Its own later toggle task is then a guaranteed-redundant echo
  // of that synchronous call and is skipped outright, so a stale task can
  // never re-evict or re-close whichever card is actually active by the
  // time it runs. A details never marked here (click/keyboard/touch, which
  // have no synchronous mouseover pre-empting them) still gets handled the
  // one time its toggle task fires, exactly as before.
  var hoverActivatedDetails = new WeakSet();

  function evictActivePanel(exceptPanel) {
    if (!activePanel || activePanel === exceptPanel) return;

    // Actually cancels the superseded network request (matching the
    // AbortController convention assets/product-info.js's own
    // renderProductInfo() already uses for this identical problem, rather
    // than a second, parallel done-flag/token scheme) — its fetch's own
    // .catch/.finally below still run, but see the AbortError there is
    // exactly what happened and skip re-showing an error for it.
    if (activeAbortController) {
      activeAbortController.abort();
      activeAbortController = null;
    }

    var panel = activePanel;
    activePanel = null;
    panel.__cqaRequestId = (panel.__cqaRequestId || 0) + 1;

    panel.dataset.loaded = 'false';
    panel.dataset.loading = 'false';
    panel.classList.remove('is-loading', 'has-error');
    if (panel.__cqaPlaceholderHtml !== undefined) {
      panel.innerHTML = panel.__cqaPlaceholderHtml;
    }
    var details = panel.closest(SELECTOR_DETAILS);
    if (details && !details.open && getState(details) !== 'adding') setState(details, 'closed');
  }

  function loadPanel(details) {
    var panel = getPanel(details);
    if (!panel) return;
    if (panel.dataset.loaded === 'true' || panel.dataset.loading === 'true') return;

    // Hands collections.css a hard, pixel cap for this card's Quick Add
    // content — the fixed media-shell height (.product-item__image's own
    // aspect-ratio box, driven by card width alone, never by Quick Add)
    // that .quick-add-inline__content's max-height reads. Deliberately NOT
    // `max-height: 100%` in that CSS: measured live that a plain 100%
    // collapses this element to ~20px in this exact position:absolute:
    // inset:0 / flex-direction:column container instead of resolving
    // against the shell's real height — a genuine, Safari-reproducible
    // bug, not a hypothetical one. A px value has no such ambiguity.
    // Measured here (not after the fetch) because the shell's height
    // depends only on the image/card width, never on Quick Add's own
    // content, so it's already correct before the fetch even starts.
    var card = details.closest(SELECTOR_HOVER_ZONE);
    var shell = card ? card.querySelector('.product-item__image') : null;
    if (card && shell) {
      card.style.setProperty('--quick-add-shell-height', shell.getBoundingClientRect().height + 'px');
    }

    // Captured once, before the very first fetch ever overwrites this panel's
    // server-rendered spinner placeholder — evictActivePanel() above restores
    // this exact markup when this panel is displaced by another, so a later
    // reopen shows the same first-open loading state rather than a blank
    // panel.
    if (panel.__cqaPlaceholderHtml === undefined) {
      panel.__cqaPlaceholderHtml = panel.innerHTML;
    }

    var productUrl = details.dataset.productUrl;
    if (!productUrl) return;

    evictActivePanel(panel);
    if (activeAbortController) activeAbortController.abort();
    activePanel = panel;
    var requestId = panel.__cqaRequestId = (panel.__cqaRequestId || 0) + 1;

    setState(details, 'loading');
    panel.dataset.loading = 'true';
    panel.classList.add('is-loading');

    var abortController = new AbortController();
    activeAbortController = abortController;

    fetch(buildRequestUrl(productUrl), {signal: abortController.signal})
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Quick Add fetch failed with status ' + response.status);
        }
        return response.text();
      })
      .then(function (html) {
        if (panel.__cqaRequestId !== requestId || activePanel !== panel || !panel.isConnected) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var content = doc.querySelector('[data-api-content]');
        if (!content) {
          throw new Error('Quick Add response was missing [data-api-content]');
        }

        panel.innerHTML = content.innerHTML;
        enhanceSwatchOverflow(panel);
        panel.dataset.loaded = 'true';
        panel.classList.remove('is-loading');

        // Align the card photo with the colour this freshly rendered panel
        // actually has checked, before the shopper touches anything.
        //
        // The card's base image is the product's featured image, while the
        // panel opens on selected_or_first_available_variant - and the two are
        // not always the same colour. Measured on
        // kadin-korse-fit-dual-zip-kapusonlu-sweat: card photo
        // ...-antrasit-1.jpg, panel "Renk Siyah". Re-clicking Siyah could not
        // repair it either, because clicking an already-checked radio fires no
        // "change" event, so the listener below never ran. One call here, on
        // the same resolver, makes the opening state correct instead of
        // relying on the shopper changing colour.
        if (details.open) syncCardImageToPanelColor(panel);

        if (details.open) setState(details, 'open');
      })
      .catch(function (error) {
        if (panel.__cqaRequestId !== requestId || activePanel !== panel || !panel.isConnected) return;
        // Expected whenever evictActivePanel() aborted this exact fetch to
        // make room for a different product's panel — that panel already
        // got its own placeholder restored above; showing an error here
        // too would be showing an error for a request nothing is waiting on.
        if (error && error.name === 'AbortError') return;

        panel.classList.remove('is-loading');
        panel.classList.add('has-error');
        panel.innerHTML = '<p class="quick-add-inline__error">' + (panel.dataset.errorText || '') + '</p>';
        if (details.open) setState(details, 'open');

        if (window.console) {
          console.warn('[collection-inline-quick-add]', error);
        }
      })
      .finally(function () {
        if (panel.__cqaRequestId !== requestId) return;
        panel.dataset.loading = 'false';
        if (activeAbortController === abortController) {
          activeAbortController = null;
        }
        // Stale-state cleanup, single choke point for all three outcomes
        // (resolved, errored, aborted-by-eviction).
        //
        // setState(details, 'loading') above runs unconditionally, so the
        // card gets CLASS_CARD_QUICK_ADD_ACTIVE even when this load was
        // started by something other than the tray opening - selecting a
        // colour on the card dispatches
        // theme:quick-add-inline:variant-changed, whose handler calls
        // loadPanel() directly while <details> is still closed. Every exit
        // from the active state required an OPEN details: closeDetails()
        // returns early on `!details.open`, closeOthers() only iterates
        // SELECTOR_DETAILS + '[open]', and forceCloseDetails() only fires on
        // confirmed cart success. So that card stayed 'loading' forever and
        // kept the active class, which hides its persistent swatch row until
        // a reload - reproduced on DEV, and not cleared by hovering any other
        // card. The AbortError path made it worse: it returns before the
        // `if (details.open)` promotion, so an evicted card could strand too.
        //
        // Resolving here instead of at each exit keeps one rule: when the
        // fetch has settled and the tray is not actually open, the card is
        // not active. 'adding' is excluded because that state owns its own
        // close (forceCloseDetails on theme:product:add).
        if (!details.open && getState(details) !== 'adding') {
          setState(details, 'closed');
        }
      });
  }

  // "+N" progressive disclosure for the colour row: past ~5 swatches the
  // row would wrap onto a second line, growing the tray beyond the
  // compact target — hide the overflow behind a small toggle instead of
  // shrinking swatches further or letting the row wrap. Purely a display
  // toggle: every swatch input (shown or hidden) stays in the DOM and
  // fully functional the whole time — clicking a hidden swatch via
  // keyboard/AT still works exactly like a visible one, this only affects
  // what's shown before someone asks to see the rest.
  var SWATCH_VISIBLE_LIMIT = 5;
  function enhanceSwatchOverflow(panel) {
    var buttons = panel.querySelectorAll('.radio__fieldset--swatches .radio__buttons > .swatch__button');
    if (buttons.length <= SWATCH_VISIBLE_LIMIT) return;

    for (var i = SWATCH_VISIBLE_LIMIT; i < buttons.length; i++) {
      buttons[i].classList.add('quick-add-inline__swatch--overflow');
    }

    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'quick-add-inline__swatch-more';
    toggle.textContent = '+' + (buttons.length - SWATCH_VISIBLE_LIMIT);
    toggle.setAttribute('aria-expanded', 'false');
    toggle.addEventListener('click', function () {
      var hidden = panel.querySelectorAll('.quick-add-inline__swatch--overflow');
      for (var j = 0; j < hidden.length; j++) {
        hidden[j].classList.remove('quick-add-inline__swatch--overflow');
      }
      toggle.remove();
    });

    buttons[SWATCH_VISIBLE_LIMIT - 1].insertAdjacentElement('afterend', toggle);
  }

  // Setting .open here queues the native "toggle" event asynchronously;
  // the delegated toggle listener further down is the single place that
  // actually calls loadPanel() and sets state — kept here so this
  // function's only job is "ask the details to open," not duplicate that
  // logic.
  function openDetails(details) {
    if (details.open) return;
    details.open = true;
  }

  // Guarded: never closes mid add-to-cart, and never closes while focus is
  // still inside the card (a keyboard user tabbing through swatches/sizes
  // must not have the panel yanked away under them).
  //
  // Deliberately does NOT clear the panel's fetched markup — only
  // evictActivePanel() (above, called from loadPanel) does that, and only
  // when a DIFFERENT panel is actually about to take its place. Closing and
  // reopening the same card with nothing else touched in between stays free
  // (cached), exactly like before this fix; the network refetch + custom-
  // element reinitialization cost is paid only on an actual cross-product
  // collision, which is the one case that needs it.
  function closeDetails(details) {
    if (!details.open) return;
    if (getState(details) === 'adding') return;
    if (details.contains(document.activeElement)) return;
    details.open = false;
    setState(details, 'closed');
  }

  // Only used on confirmed cart success, bypassing the "adding" guard above
  // on purpose — this IS the transition out of "adding".
  function forceCloseDetails(details) {
    details.open = false;
    setState(details, 'closed');
  }

  function closeOthers(current) {
    document.querySelectorAll(SELECTOR_DETAILS + '[open]').forEach(function (other) {
      if (other === current) return;
      if (getState(other) === 'adding') return;
      other.open = false;
      setState(other, 'closed');
    });
  }

  // FINAL desktop interaction (superseding the explicit-button pass this
  // replaces): clicking an AVAILABLE size directly adds that resolved
  // variant to cart — no separate visible Add to Cart control, which was
  // itself one of the things making the tray read as a mini PDP rather
  // than a genuinely quick, compact action. The real <button
  // data-add-to-cart> stays in the DOM — collections.css hides it visually
  // (.visually-hidden, not display:none) so it's still reachable by
  // keyboard/AT as a manual fallback — this file only ever *clicks* it
  // programmatically, so the actual submit path (form, theme:cart:add,
  // theme:product:add/-error handling below) is completely unchanged.
  // Mobile's own bottom sheet is separate markup entirely
  // (product-quick-add-modal-template.liquid) and keeps its own explicit
  // button — this desktop-only change doesn't touch it.
  function attemptDesktopSubmit(panel, details) {
    var submitButton = panel.querySelector(SELECTOR_SUBMIT);
    if (!submitButton || submitButton.hasAttribute('disabled')) return;

    setState(details, 'adding');
    details.setAttribute('aria-busy', 'true');
    submitButton.click();
  }

  // A click on an available size arms a one-shot wait for product-info's
  // real "variant resolved" signal — the `change` event it dispatches on
  // input[name=id] once its AJAX re-fetch finishes (updateVariantInputs in
  // product-info.js) — rather than reading the variant id synchronously on
  // click, which would race that resolution (changing size can also change
  // which sold-out states/price product-info updates, all before the id
  // input itself reflects the newly chosen size).
  document.addEventListener(
    'change',
    function (event) {
      var panel = event.target.closest ? event.target.closest(SELECTOR_PANEL) : null;
      if (!panel) return;
      var sizeInput = event.target.closest ? event.target.closest(SELECTOR_SIZE_FIELDSET + ' input[type="radio"]') : null;
      if (!sizeInput || sizeInput.classList.contains('disabled')) return;

      var details = panel.closest(SELECTOR_DETAILS);
      if (!details || getState(details) === 'adding') return;

      panel.dataset.awaitingVariant = 'true';
    },
    true
  );

  // A failed size fetch must not leave an auto-submit armed for a later
  // colour change. The shopper retries by explicitly choosing a size.
  document.addEventListener('theme:product-info:selection-restored', function (event) {
    var panel = event.target.closest ? event.target.closest(SELECTOR_PANEL) : null;
    if (panel) panel.dataset.awaitingVariant = 'false';
  });

  // Fires on every input[name=id] change inside any panel — including the
  // one product-info itself dispatches after a successful re-fetch. Only
  // acts when a size click armed it above, so a plain colour change (which
  // also updates input[name=id]) never auto-submits on its own.
  document.addEventListener(
    'change',
    function (event) {
      var panel = event.target.closest ? event.target.closest(SELECTOR_PANEL) : null;
      if (!panel || panel.dataset.awaitingVariant !== 'true') return;
      if (event.target.name !== 'id') return;

      panel.dataset.awaitingVariant = 'false';

      var details = panel.closest(SELECTOR_DETAILS);

      // product-info.js dispatches this same "change" on input[name=id]
      // from inside updateVariantInputs() — which runs BEFORE the rest of
      // handleUpdateProductInfo() sets the submit button's disabled state
      // further down that same (synchronous) function. Reading `disabled`
      // right here would still see the PREVIOUS variant's state.
      // queueMicrotask defers this to immediately after that function
      // finishes — not a guessed delay, just "after the current script
      // completes."
      queueMicrotask(function () {
        attemptDesktopSubmit(panel, details);
      });
    },
    true
  );

  // Single-usable-size edge case: a size fieldset with exactly one enabled
  // radio arrives already `checked` (product-variant-options.liquid
  // renders `checked` whenever value.selected is true, which for a lone
  // enabled value is always true) — clicking an already-checked radio
  // never fires `change` (no state transition for the browser to report),
  // so the listener above never arms for it. This click-driven fallback
  // only ever runs in exactly that case: a fieldset with 2+ enabled sizes
  // always has a real `change` available to reach the listener above
  // instead, so the two paths never both fire for the same click.
  // The theme preselects the first available size, so the size that LOOKS
  // selected is a valid click target too - but clicking an already-checked
  // radio never fires `change`, so the change-driven path above never arms
  // for it and the tap did nothing. A press on a size's label/input records
  // whether that size was already checked BEFORE the click flips anything;
  // the click handler below then treats that "re-selection" like the
  // single-usable-size case (variant already resolved, submit immediately).
  // Pressing a DIFFERENT size records `false`, so that case keeps using the
  // change-driven path exactly as before (never both paths for one click).
  var sizePressedWasChecked = null;
  function recordSizePress(event) {
    sizePressedWasChecked = null;
    var node = event.target && event.target.closest ? event.target.closest(SELECTOR_PANEL + ' ' + SELECTOR_SIZE_FIELDSET + ' .radio__button') : null;
    if (!node) return;
    var input = node.querySelector('input[type="radio"]');
    if (input) sizePressedWasChecked = input.checked;
  }
  document.addEventListener('pointerdown', recordSizePress, true);
  document.addEventListener('keydown', function (event) {
    if (event.key === ' ' || event.key === 'Enter') recordSizePress(event);
  }, true);

  document.addEventListener('click', function (event) {
    var sizeInput = event.target.closest ? event.target.closest(SELECTOR_PANEL + ' ' + SELECTOR_SIZE_FIELDSET + ' input[type="radio"]') : null;
    if (!sizeInput || sizeInput.classList.contains('disabled')) return;

    var fieldset = sizeInput.closest(SELECTOR_SIZE_FIELDSET);
    if (!fieldset) return;

    var reselected = sizePressedWasChecked === true;
    sizePressedWasChecked = null;

    var usableSizes = Array.prototype.filter.call(fieldset.querySelectorAll('input[type="radio"]'), function (input) {
      return !input.classList.contains('disabled');
    });
    if (usableSizes.length !== 1 && !reselected) return;

    var panel = sizeInput.closest(SELECTOR_PANEL);
    var details = panel ? panel.closest(SELECTOR_DETAILS) : null;
    if (!details || getState(details) === 'adding') return;

    queueMicrotask(function () {
      attemptDesktopSubmit(panel, details);
    });
  });

  // Keyboard/AT fallback: the visually-hidden submit button is still a
  // real, focusable, tabbable element (collections.css .visually-hidden,
  // not display:none) — a direct Enter/Space activation on it (not routed
  // through either size-click path above) still gives the normal
  // "adding" feedback before the theme's own cart JS takes over.
  document.addEventListener('click', function (event) {
    var submitButton = event.target.closest ? event.target.closest(SELECTOR_PANEL + ' ' + SELECTOR_SUBMIT) : null;
    if (!submitButton || submitButton.hasAttribute('disabled')) return;

    var details = submitButton.closest(SELECTOR_DETAILS);
    if (!details) return;

    setState(details, 'adding');
    details.setAttribute('aria-busy', 'true');
  });

  // Card-photo sync — separate listener, separate concern from the click
  // listener above: purely visual, fires on EVERY option change (color
  // included) via a different event ("change" here vs. "click" above), and
  // never touches state or the submit button. Independent of it, not a
  // replacement for it.
  //
  // Root cause this addresses: the fetched panel has its own real, working
  // image gallery, but it's intentionally hidden (see collections.css,
  // ".quick-add-inline__panel .product-quick-add__images") so Quick Add
  // doesn't show a second, redundant photo next to the card's own. That
  // left the panel's color selection with nothing wired to the card's
  // ALREADY-VISIBLE photo, which is otherwise only ever updated by
  // GridSwatch (theme.dev.js) hovering the card's own separate outer
  // swatch row — a different set of elements the panel's color radios
  // never interact with. This reuses that exact same mechanism (same
  // selector, same "is-visible" class, same per-card [data-product-image]
  // stack the theme already renders and keeps responsive) instead of
  // building a second one.
  //
  // Timing: input[name=id]'s "change" is dispatched by product-info.js's
  // updateVariantInputs() — which runs AFTER updateOptionValues() has
  // already swapped in the freshly-fetched <variant-selects> (including a
  // freshly server-rendered, correctly-checked swatch radio) via
  // HTMLUpdateUtility.viewTransition. That swap inserts the new node as
  // the PRECEDING sibling of the old one and only removes the old one
  // after a 500ms setTimeout (see theme.dev.js) — so for up to 500ms two
  // <variant-selects> can coexist, but querySelector() always returns the
  // first match in document order, which is the new (current) one, never
  // the old one awaiting removal. So the panel's OWN currently-checked
  // swatch is already correct by the time this listener runs — the
  // queueMicrotask below is not strictly required for that reason, but
  // it's kept for consistency with the rest of this file and to stay
  // clear of any same-tick reentrancy.
  document.addEventListener(
    'change',
    function (event) {
      var panel = event.target.closest ? event.target.closest(SELECTOR_PANEL) : null;
      if (!panel) return;

      // Two triggers, same handler, because neither alone is sufficient:
      //
      // - input[name=id] "change" is the original one, dispatched by
      //   product-info.js's updateVariantInputs() after the freshly fetched
      //   <variant-selects> is in place.
      // - the colour radio's own "change" is the deterministic one. Since
      //   DEV-QUICK-ADD-EXPLICIT-SIZE-001 the panel deliberately holds every
      //   size unselected, so no variant resolves from a colour click alone
      //   and there is no guarantee [name=id] changes value - and a "change"
      //   event only fires when the value actually changed. Observed on
      //   kadin-korse-fit-dual-zip-kapusonlu-sweat: the card kept the base
      //   Antrasit photo after Siyah was selected, while the stack did carry
      //   a Siyah entry.
      //
      // Both land in the same syncCardImageToPanelColor(), which reads the
      // panel's own currently-checked swatch - so a double fire is idempotent,
      // not a second code path, and no second resolver exists.
      var isVariantId = event.target.name === 'id';
      var isColourSwatch =
        event.target.type === 'radio' &&
        !!event.target.closest('.radio__fieldset--swatches');
      if (!isVariantId && !isColourSwatch) return;

      queueMicrotask(function () {
        syncCardImageToPanelColor(panel);
      });
    },
    true
  );

  function syncCardImageToPanelColor(panel) {
    // The color-image stack (data-variant-title) is deduplicated BY PHOTO
    // in product-grid-item.liquid, one entry per unique image — each
    // entry's data-variant-title is a FULL variant title (e.g.
    // "Siyah / M"), coming from whichever variant Shopify's swatch-option
    // accessor happened to return for that color, which is NOT
    // necessarily the size the shopper has selected here. So this reads
    // the panel's own currently-checked color swatch value directly
    // (e.g. "Siyah") and matches it against each candidate's title split
    // into its " / "-separated segments, rather than ever comparing full
    // titles — a selected size that differs from the stack's embedded one
    // must still match on color.
    var checkedSwatch = panel.querySelector(SELECTOR_CHECKED_SWATCH);
    var colorName = checkedSwatch ? checkedSwatch.value : null;

    // Scoped to THIS panel's own ancestor card only — never a global
    // lookup — so a stale/in-flight response for one card can never touch
    // another card's photo, including two instances of the same product
    // repeated across homepage sections or the PLP.
    var details = panel.closest(SELECTOR_DETAILS);
    var card = details ? details.closest(SELECTOR_HOVER_ZONE) : null;
    var productImage = card ? card.querySelector(SELECTOR_PRODUCT_IMAGE) : null;
    var notice = card ? card.querySelector(SELECTOR_IMAGE_UNAVAILABLE_NOTICE) : null;

    if (!colorName) {
      if (notice) notice.hidden = true;
      return;
    }
    // An explicit colour choice exists from here on: the colour-synced photo is
    // now the authoritative one for this card, so the hover layer above it is
    // suppressed (collections.css) until the card closes.
    if (card) card.classList.add(CLASS_CARD_COLOUR_SELECTED);
    if (!productImage) return;

    var variantImages = productImage.querySelectorAll('[' + ATTR_VARIANT_TITLE + ']');
    // No per-color image stack rendered for this product (e.g. image-hover
    // disabled in theme settings, or no swatch-type color option at all)
    // — nothing verified to sync, and nothing invented in its place. This
    // product has no color-photo mapping at all, so the "unavailable for
    // THIS color" notice (which implies other colors DO have one) would be
    // misleading here too — leave it hidden rather than show it.
    if (!variantImages.length) {
      if (notice) notice.hidden = true;
      return;
    }

    var match = null;
    for (var i = 0; i < variantImages.length; i++) {
      var segments = (variantImages[i].getAttribute(ATTR_VARIANT_TITLE) || '').split(' / ');
      if (segments.indexOf(colorName) !== -1) {
        match = variantImages[i];
        break;
      }
    }

    // The notice is never correct, so it is never shown.
    //
    // "No match in the stack" does NOT mean "this colour has no photo". The
    // stack in product-grid-item.liquid is deduplicated BY PHOTO and seeded
    // with the card's own card_featured_image, so the colour the base image
    // already represents - the default colour - deliberately gets no overlay
    // of its own. Measured on /collections/tum-urunler: 13 of 19 products
    // have an overlay stack, and in every one of them exactly one colour is
    // absent from it, and in every case that colour is the default. Selecting
    // it here therefore matched nothing and un-hid a notice reading "Bu renk
    // için görsel mevcut değil" over a card whose base image was that very
    // colour, correct and fully opaque (baseOpacity 1). Because the notice is
    // `inset: 0; background: var(--bg)` it covered that good photo with a
    // solid panel - the blank card the merchant reported.
    //
    // The remaining no-match case, a colour with genuinely no distinct photo,
    // still leaves the base product image on screen, which is a valid product
    // photo. So in both branches a real image is visible and the notice would
    // be false. Matching stays exactly as it was; only this false customer-
    // facing state is removed. The element and its locale key are left in
    // place rather than ripped out, so nothing else in the card or the
    // translations shifts.
    if (notice) notice.hidden = true;

    variantImages.forEach(function (image) {
      image.classList.remove(CLASS_IMAGE_VISIBLE);
    });
    // No verified match for this color: leave every stacked image hidden
    // rather than show an unrelated one — the card's own always-present
    // default image (data-product-image-default) shows through underneath,
    // exactly as it already does whenever GridSwatch's own hover leaves no
    // match (see hideVariantImages in theme.dev.js).
    if (match) match.classList.add(CLASS_IMAGE_VISIBLE);
    // The outer swatch adapter also owns this state. Clear it when the
    // panel returns to the base colour; otherwise CSS hides the base while
    // this controller has already hidden every overlay.
    if (card) card.classList.toggle('has-selected-swatch-media', !!match);
    if (!match) {
      var baseFigure = productImage.querySelector('[data-product-image-default] > figure');
      if (baseFigure) baseFigure.classList.add('aos-animate');
    }
  }

  // Product-card cleanup: colour is now selected on the card (GridSwatch's
  // selectSwatch in theme.dev.js/theme.js), not inside this panel. That
  // click updates the <details data-quick-add-inline> element's own
  // data-product-url and dispatches this event — handled here, not read
  // directly by GridSwatch, because this file owns the panel's private
  // loaded/loading cache and is the only place that should decide when to
  // re-fetch it.
  //
  // Invalidation, not just "use the new URL next time": hovering ANYWHERE
  // on the card — SELECTOR_HOVER_ZONE is the whole .product-item, swatches
  // included — already starts loading this panel with the DEFAULT colour
  // before a click on a swatch can happen, so by the time this event fires
  // the panel is very likely already loading or already cached with the
  // wrong colour. Resetting both dataset flags and calling loadPanel()
  // again unconditionally covers "never opened yet" (harmless - the flags
  // were already false), "currently loading" (the in-flight fetch's own
  // .then() will still land and get overwritten by this fresh one - no
  // guard against overlapping requests existed before this either, so this
  // doesn't introduce a new failure mode) and "already loaded, showing the
  // wrong colour" (the case this exists for) with one code path.
  document.addEventListener('theme:quick-add-inline:variant-changed', function (event) {
    var details = event.detail && event.detail.details;
    if (!details) return;

    var panel = getPanel(details);
    if (!panel) return;

    panel.dataset.loaded = 'false';
    panel.dataset.loading = 'false';
    loadPanel(details);
  });

  // theme:product:add / theme:product:add-error — dispatched ON THE
  // SUBMIT BUTTON by the theme's existing cart JS (bubbles: true), the
  // instant /cart/add.js responds. This is the real, deterministic
  // success/failure signal — not a delay, not an observer.
  document.addEventListener('theme:product:add', function (event) {
    var panel = event.target.closest ? event.target.closest(SELECTOR_PANEL) : null;
    if (!panel) return;

    var details = panel.closest(SELECTOR_DETAILS);
    if (details) {
      details.removeAttribute('aria-busy');
      // Cart JS's own getCart()/drawer render happens synchronously after
      // this event; closing now (real success confirmed, not assumed)
      // matches "only after the drawer opens may Quick Add close."
      forceCloseDetails(details);
    }
  });

  document.addEventListener('theme:product:add-error', function (event) {
    var panel = event.target.closest ? event.target.closest(SELECTOR_PANEL) : null;
    if (!panel) return;

    var details = panel.closest(SELECTOR_DETAILS);
    if (details) {
      details.removeAttribute('aria-busy');
      // Stay open on failure — chosen options and the theme's own native
      // error message (product__form__errors, populated by
      // addToCartError in theme.dev.js) both remain visible.
      setState(details, 'open');
    }
  });

  // The actual "this card becomes the active open one" work — shared by
  // the toggle listener (click/keyboard/touch opens, and the deferred echo
  // of a hover open) and the mouseover listener (hover opens, called
  // synchronously there for fetch-on-intent perf; see hoverActivatedDetails
  // above for why the toggle listener must not repeat this for the same
  // open).
  function activateDetails(details) {
    // loadPanel() only runs (and only sets state) the first time a card
    // is opened; reopening an already-cached card needs its own
    // explicit transition back to "open" here.
    var panel = getPanel(details);
    if (panel && panel.dataset.loaded === 'true') {
      setState(details, 'open');
      syncCardImageToPanelColor(panel);
    } else {
      loadPanel(details);
    }
    closeOthers(details);
  }

  // `toggle` does not bubble, so delegation requires the capture phase.
  // Fires for hover-driven opens (below), clicking the trigger, and
  // keyboard activation (Enter/Space on the focused <summary>) alike —
  // one code path loads the panel and keeps aria-expanded correct
  // regardless of how the card was opened.
  document.addEventListener(
    'toggle',
    function (event) {
      var details = event.target.closest ? event.target.closest(SELECTOR_DETAILS) : null;
      if (!details) return;

      var summary = details.querySelector('summary');
      if (summary) {
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      }

      if (!details.open) {
        // Native toggle-close: clicking an open <summary> again (or Esc on
        // the focused summary) closes the <details> with no JS call in
        // between, so nothing here reset the card - data-state stayed "open"
        // and the card kept .quick-add-inline-active, which is the class the
        // CSS uses to hide the colour swatches. Measured live at 1440 after a
        // single open + trigger-click close: state="open", details.open=false,
        // 0 swatches visible, holder display:none. The hover-out path already
        // goes through closeDetails()/forceCloseDetails(), which set "closed"
        // themselves; this is the one close path that had no owner.
        //
        // An in-flight add keeps its own "adding" state so the button's
        // feedback is not cancelled by a close.
        if (getState(details) !== 'adding') setState(details, 'closed');
        return;
      }

      // This exact open was already fully handled, synchronously, by the
      // mouseover listener below — this queued task is its guaranteed-
      // redundant echo. Skipping it (rather than letting activateDetails
      // run again) is what stops a stale task from re-evicting/re-closing
      // whichever card actually became active in the meantime; see
      // hoverActivatedDetails above.
      if (hoverActivatedDetails.has(details)) {
        hoverActivatedDetails.delete(details);
        return;
      }

      activateDetails(details);
    },
    true
  );

  // Desktop hover: the whole product card (SELECTOR_HOVER_ZONE, above) is
  // the hover zone — not just the <details>, which is small/bottom-anchored
  // while closed, and not just the image, so hovering the title/price area
  // also opens it. This is a plain hover LISTENER, not an overlay, so the
  // existing image link underneath is completely unaffected and still
  // navigates normally.
  function detailsInZone(zone) {
    return zone.querySelector(SELECTOR_DETAILS);
  }

  // One pointerleave listener per card, bound the first time it's hovered.
  // See the file-level comment above for why pointerleave (not
  // mouseout/relatedTarget) is the durable boundary for this component.
  var boundCards = new WeakSet();

  function ensureCardExitListener(card) {
    if (boundCards.has(card)) return;
    boundCards.add(card);

    card.addEventListener('pointerleave', function () {
      // closeDetails() already guards against closing while add-to-cart is
      // in progress or while focus is still inside the card — unchanged,
      // not duplicated here.
      var details = detailsInZone(card);
      if (details) closeDetails(details);
    });
  }

  // Root cause of the card row shifting during Quick Add use: on the
  // homepage this card sits inside <grid-slider> (theme.dev.js), whose
  // DraggableSlider unconditionally treats any mousedown+mousemove ANYWHERE
  // inside the slider — including on our own swatch/size controls, since
  // they're descendants of it — as a drag-to-scroll gesture, moving the
  // whole row on ordinary clicks. DraggableSlider isn't ours to edit
  // (theme.dev.js). A bubble-phase mousedown listener on the <details>
  // element itself — a stable, always-server-rendered node, present before
  // the panel ever loads — runs before the slider's own bubble listener
  // (bubble order goes target-outward, and details sits between the target
  // and the slider), so stopping propagation here reaches the slider's
  // handler never, while the click still completes normally on whatever it
  // actually landed on. Bound lazily on first hover, same WeakSet pattern
  // as the exit listener above, so cards added later by an AJAX
  // filter/sort are covered the same way. Scoped to the whole Quick Add
  // island (not just swatches/sizes) since none of it should ever drag the
  // page underneath it.
  //
  // Disclosed, accepted side effect: theme.dev.js also has a generic,
  // unrelated document-level mousedown listener that clears a
  // keyboard-focus-ring class on the <body> for any mousedown anywhere on
  // the page. Stopping propagation here means that specific bookkeeping
  // doesn't run for clicks inside Quick Add - a cosmetic focus-ring nuance,
  // not a functional loss, and far smaller than the row-shifting bug it
  // replaces. Not global: every other click on the page still reaches it.
  var boundMousedownGuards = new WeakSet();

  function ensureMousedownGuard(details) {
    if (boundMousedownGuards.has(details)) return;
    boundMousedownGuards.add(details);

    details.addEventListener('mousedown', function (event) {
      event.stopPropagation();
    });
  }

  // Demonstrated gap in the mouseover-triggered attachment below, not a
  // hypothetical one: mouseover only fires when the pointer crosses INTO an
  // element's bounds — it is never dispatched for content that appears
  // under a pointer that is already stationary there (true on first page
  // load if the cursor happens to already rest over a card, and would
  // equally be true for a card an AJAX filter/sort inserts under a
  // motionless cursor). Attaching to every [data-quick-add-inline] already
  // present the moment this script runs removes the timing dependency
  // entirely for initial load, which is the common case; a card injected
  // later by an AJAX filter/sort still relies on the mouseover path below
  // and keeps this same narrow edge case until it is next actually
  // hovered — disclosed, not silently left implied as fixed.
  //
  // Touch input is a separate question this does not need to cover:
  // GridSlider (theme.dev.js) only constructs DraggableSlider when
  // `!theme.isMobile || !window.theme.touch` — on an actual touch device it
  // is never instantiated at all, so there is no mousedown-drag conflict
  // for this guard to prevent there in the first place.
  document.querySelectorAll(SELECTOR_DETAILS).forEach(ensureMousedownGuard);

  document.addEventListener('mouseover', function (event) {
    var zone = event.target.closest ? event.target.closest(SELECTOR_HOVER_ZONE) : null;
    if (!zone) return;
    var details = detailsInZone(zone);
    if (!details) return;
    ensureCardExitListener(zone);
    ensureMousedownGuard(details);
    // Setting .open here only *queues* the native "toggle" event
    // asynchronously (see openDetails' own comment) — activateDetails() is
    // called directly here too, on this same synchronous mouseover, so the
    // fetch starts at the earliest real purchase-intent signal instead of
    // waiting on that queued event. Safe to call unconditionally: it
    // already no-ops via panel.dataset.loaded/loading if a fetch for this
    // product is in flight or done (see loadPanel below), so this never
    // duplicates a request. hoverActivatedDetails marks this open as
    // already fully handled so the toggle listener's own later call to it,
    // once that queued event does fire, is skipped instead of repeated —
    // see hoverActivatedDetails' own comment for why that skip matters.
    activateDetails(details);
    hoverActivatedDetails.add(details);
    openDetails(details);
  });

  // Mobile Quick Add trigger recovery (the theme's own QuickAddProduct, not
  // this file's desktop tray). Its click handler disables the trigger and adds
  // "is-loading" BEFORE fetching the product for the bottom sheet, and only
  // modalOpen() ever undoes that: renderModal() has no .catch, so a failed or
  // non-OK fetch (network drop, throttling) left that product's trigger
  // disabled for the rest of the page visit and every later tap did nothing
  // (reproduced by failing that one fetch; other products kept working). If
  // the sheet has not opened after the timeout, the trigger is given back so
  // the shopper can tap again. A slow-but-successful fetch is unaffected: the
  // theme resets the trigger itself when the sheet opens.
  var MOBILE_TRIGGER = '.quick-add-inline__mobile-trigger .look__dot__button';
  var MOBILE_TRIGGER_TIMEOUT = 10000;
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest ? event.target.closest(MOBILE_TRIGGER) : null;
    if (!trigger) return;
    window.setTimeout(function () {
      if (!trigger.isConnected) return;
      if (trigger.disabled && trigger.classList.contains('is-loading')) {
        trigger.disabled = false;
        trigger.classList.remove('is-loading');
      }
    }, MOBILE_TRIGGER_TIMEOUT);
  });
})();
